import { Injectable } from '@angular/core';
import { ApiInterface, ApiQueryParamsDTO } from '../api-interface';
import { ApiService } from '../api.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NotaDTO, NotaEmFaturaDTO, PageNotaConsultaDTO, ResumoCancelamentoNotaDTO, VendaFiltrosDTO } from '../../models/nota.model';

@Injectable({
  providedIn: 'root',
})
export class NotaService implements ApiInterface {
  constructor(private apiService: ApiService) { }

  get<T>(
    url: string,
    data?: ApiQueryParamsDTO
  ): Observable<PageNotaConsultaDTO> {
    const queryParams = this.apiService.buildQueryParams(data);
    const patch = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<PageNotaConsultaDTO>(patch);
  }

  getFilter<T>(
    url: string,
    data?: { pessoa_id: number } & ApiQueryParamsDTO
  ): Observable<PageNotaConsultaDTO> {
    const queryParams = this.apiService.buildQueryParams(data);
    const patch = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<PageNotaConsultaDTO>(patch);
  }

  getById<T>(url: string, id: string): Observable<NotaDTO> {
    return this.apiService.read<NotaDTO>(`${url}/${id}`);
  }

  post<T>(url: string, data: NotaDTO): Observable<NotaDTO> {
    return this.apiService.create<NotaDTO>(url, data);
  }

  /**
   * POST para criação de venda que retorna apenas o ID.
   * Usa responseType 'text' para evitar erro de parsing JSON
   * em respostas grandes (imagens base64 nos itens).
   */
  postParaVenda(url: string, data: any): Observable<{ id: number }> {
    return this.apiService.postText(url, data).pipe(
      map((text: string) => {
        const idMatch = text.match(/"id"\s*:\s*(\d+)/);
        return { id: idMatch ? parseInt(idMatch[1], 10) : 0 };
      })
    );
  }

  /**
   * Igual ao `postParaVenda`, mas SEM herdar o contexto de cliente do contador
   * (não injeta `X-Empresa-Contexto`). Usado no faturamento de parcelas: a venda
   * é criada na PRÓPRIA empresa do contador, não na do cliente ativo.
   */
  postParaVendaSemContextoContador(url: string, data: any): Observable<{ id: number }> {
    return this.apiService.postTextSemContextoContador(url, data).pipe(
      map((text: string) => {
        const idMatch = text.match(/"id"\s*:\s*(\d+)/);
        return { id: idMatch ? parseInt(idMatch[1], 10) : 0 };
      })
    );
  }

  transformarNotaEmFatura(url: string, data: NotaEmFaturaDTO): Observable<NotaDTO> {
    return this.apiService.update<NotaDTO>(url, data);
  }

  gerarPdfNota(url: string): Observable<string> {
    return this.apiService.postText(url);
  }

  /**
   * Gera o PDF da ficha do evento de uma venda (base64).
   * Endpoint GET; o backend decide o template pela configuracao da empresa
   * (Tipo de Ficha do Evento), por isso o frontend envia apenas o notaId.
   */
  gerarPdfFicha(notaId: number): Observable<string> {
    return this.apiService.readText(`/relatorios/imprimir-fichas/${notaId}`);
  }

  put<T>(url: string, data: NotaDTO): Observable<NotaDTO> {
    return this.apiService.update<NotaDTO>(url, data);
  }

  /**
   * Cancela a nota no sistema (cancelamento interno, sem emissao ao fisco).
   *
   * Usado para nota que NAO e fatura legal eletronica aprovada. Quando a nota tem NF-e
   * aprovada, o cancelamento correto e o fiscal (`/nfe/cancelar`), que o proprio backend
   * encadeia com o cancelamento interno depois do retorno do fisco.
   *
   * O backend recebe o motivo por query param e nao espera corpo na requisicao.
   */
  cancelarNota(
    notaId: number,
    motivoDeCancelamento: string,
    confirmaEstornoDePagamentos = false
  ): Observable<NotaDTO> {
    const motivo = encodeURIComponent(motivoDeCancelamento);
    return this.apiService.update<NotaDTO>(
      `/nota/cancelar/${notaId}?motivoDeCancelamento=${motivo}&confirmaEstornoDePagamentos=${confirmaEstornoDePagamentos}`,
      undefined
    );
  }

  /**
   * Consulta o que sera estornado ao cancelar a nota (parcelas pagas e movimentos gerados).
   *
   * Chamado antes de abrir o dialogo de cancelamento: com `possuiPagamento` verdadeiro o usuario
   * ve o popup de confirmacao de estorno; falso, o dialogo simples de motivo. Consulta pura.
   */
  resumoDeCancelamento(notaId: number): Observable<ResumoCancelamentoNotaDTO> {
    return this.apiService.read<ResumoCancelamentoNotaDTO>(`/nota/resumo-cancelamento/${notaId}`);
  }

  delete<T>(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  /**
   * Busca notas com filtros avancados server-side.
   * Monta query params a partir do VendaFiltrosDTO, omitindo campos undefined.
   */
  buscarComFiltrosAvancados(
    filtros: VendaFiltrosDTO,
    tipoDeTransacao: string,
    page: number,
    size: number,
    sort: string,
    searchText?: string
  ): Observable<PageNotaConsultaDTO> {
    let params = `page=${page}&size=${size}&sort=${sort}`;
    params += `&tipoDeTransacao=${tipoDeTransacao}`;

    // Filtros de texto (busca local mantida)
    if (searchText) {
      if (!isNaN(Number(searchText))) {
        params += `&codigoNota=${searchText}`;
      }
      params += `&nomePessoa=${searchText}`;
      params += `&nomeVendedor=${searchText}`;
    }

    // Filtros avancados server-side
    if (filtros.notaId != null) params += `&notaId=${filtros.notaId}`;
    if (filtros.numeroFatura != null) params += `&numeroFatura=${filtros.numeroFatura}`;
    if (filtros.pessoaId != null) params += `&pessoaId=${filtros.pessoaId}`;
    if (filtros.observacao) params += `&observacao=${encodeURIComponent(filtros.observacao)}`;
    if (filtros.cancelado != null) params += `&cancelado=${filtros.cancelado}`;
    if (filtros.dataHoraInicial) params += `&dataInicio=${filtros.dataHoraInicial}`;
    if (filtros.dataHoraFinal) params += `&dataFim=${filtros.dataHoraFinal}`;

    return this.apiService.read<PageNotaConsultaDTO>(`/nota/buscar?${params}`);
  }
}
